import React from "react";
import { motion } from "framer-motion";
import { Award, Plus, Edit, Trash2, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Certification } from "./types";

interface CertificationsCardProps {
	certifications: Certification[];
	newCertification: {
		name: string;
		description: string;
		issue_date: string;
		certificate_url: string;
	};
	uploadingCertificate: boolean;
	onNewCertificationChange: (field: string, value: string) => void;
	onAddCertification: () => void;
	onEditCertification: (cert: Certification) => void;
	onDeleteCertification: (id: string, name: string) => void;
	onCertificateUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export default function CertificationsCard({
	certifications,
	newCertification,
	uploadingCertificate,
	onNewCertificationChange,
	onAddCertification,
	onEditCertification,
	onDeleteCertification,
	onCertificateUpload,
}: CertificationsCardProps) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6, delay: 0.7 }}
			className="mt-8"
		>
			<Card className="glass-card border-0 shadow-xl">
				<CardHeader className="text-center pb-6">
					<div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
						<Award className="h-8 w-8 text-newTheme-orange" />
					</div>
					<CardTitle className="text-2xl font-bold text-black font-karla">
						Professional Certifications
					</CardTitle>
					<p className="text-newTheme-grey font-karla mt-2 max-w-2xl mx-auto">
						Showcase your professional certifications and training to build trust with families.
					</p>
				</CardHeader>
				<CardContent className="space-y-8">
					{/* Add New Certification */}
					<div className="bg-primary-50 rounded-lg p-4">
						<h3 className="font-semibold text-gray-900 font-karla mb-4">
							Add New Certification
						</h3>
						<div className="space-y-4">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<Label
										htmlFor="certName"
										className="text-sm font-medium text-newTheme-grey font-karla"
									>
										Certification Name
									</Label>
									<Input
										id="certName"
										placeholder="e.g., Certified Birth Doula"
										value={newCertification.name}
										onChange={(e) =>
											onNewCertificationChange("name", e.target.value)
										}
										className="mt-1"
									/>
								</div>
								<div>
									<Label
										htmlFor="certDate"
										className="text-sm font-medium text-newTheme-grey font-karla"
									>
										Issue Date
									</Label>
									<Input
										id="certDate"
										type="date"
										value={newCertification.issue_date}
										onChange={(e) =>
											onNewCertificationChange("issue_date", e.target.value)
										}
										className="mt-1"
									/>
								</div>
							</div>
							<div>
								<Label
									htmlFor="certDescription"
									className="text-sm font-medium text-newTheme-grey font-karla"
								>
									Description
								</Label>
								<Textarea
									id="certDescription"
									placeholder="Describe this certification..."
									value={newCertification.description}
									onChange={(e) =>
										onNewCertificationChange("description", e.target.value)
									}
									rows={3}
									className="mt-1"
								/>
							</div>

							{/* Certificate File Upload */}
							<div>
								<Label className="text-sm font-medium text-newTheme-grey font-karla">
									Certificate File
								</Label>
								<div className="mt-2">
									<input
										type="file"
										accept=".pdf,.jpg,.jpeg,.png"
										onChange={onCertificateUpload}
										className="hidden"
										id="certificate-upload"
									/>
									<label
										htmlFor="certificate-upload"
										className="inline-flex items-center px-4 py-2 border border-newTheme-lightGrey rounded-md shadow-btn-shadow text-sm font-medium text-black bg-white hover:bg-gray-50 cursor-pointer"
									>
										<FileText className="h-4 w-4 mr-2" />
										{uploadingCertificate
											? "Uploading..."
											: "Upload Certificate"}
									</label>
									{newCertification.certificate_url && (
										<p className="mt-2 text-sm text-green-600 font-karla">
											Certificate uploaded successfully!
										</p>
									)}
								</div>
							</div>
						</div>
						<Button
							onClick={onAddCertification}
							className="mt-4 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow"
							disabled={
								!newCertification.name.trim() || !newCertification.issue_date
							}
						>
							<Plus className="h-4 w-4 mr-2" />
							Add Certification
						</Button>
					</div>

					{/* Existing Certifications */}
					<div>
						<h3 className="font-semibold text-gray-900 font-karla mb-4">
							Your Certifications
						</h3>
						{certifications.length > 0 ? (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								{certifications.map((cert) => (
									<Card
										key={cert.id}
										className="border border-gray-200 hover:shadow-md transition-shadow"
									>
										<CardContent className="p-6">
											<div className="flex items-start space-x-4">
												<div className="bg-primary-100 p-2 rounded-lg">
													<Award className="h-6 w-6 text-primary-600" />
												</div>
												<div className="flex-1">
													<h4 className="font-semibold text-gray-900 font-karla">
														{cert.name}
													</h4>
													<p className="text-sm text-gray-600 font-karla mt-1">
														Issued:{" "}
														{new Date(cert.issue_date).toLocaleDateString()}
													</p>
													<p className="text-gray-600 font-karla text-sm mt-2">
														{cert.description}
													</p>
													{cert.certificate_url && (
														<a
															href={cert.certificate_url}
															target="_blank"
															rel="noopener noreferrer"
															className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-karla mt-2"
														>
															<FileText className="h-4 w-4 mr-1" />
															View Certificate
														</a>
													)}
												</div>
											</div>
											<div className="flex space-x-2 mt-4">
												<Button
													variant="outline"
													size="sm"
													onClick={() => onEditCertification(cert)}
													className="flex-1 border-newTheme-lightGrey text-black hover:bg-gray-50 shadow-btn-shadow hover:text-black"
												>
													<Edit className="h-4 w-4 mr-1" />
													Edit
												</Button>
												<Button
													variant="outline"
													size="sm"
													onClick={() =>
														onDeleteCertification(cert.id, cert.name)
													}
													className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-600 shadow-btn-shadow"
												>
													<Trash2 className="h-4 w-4 mr-1" />
													Delete
												</Button>
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						) : (
							<div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
								<Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
								<p className="text-gray-500 font-karla text-lg">
									No certifications added yet
								</p>
								<p className="text-gray-400 font-karla text-sm mt-2">
									Add your professional certifications to build trust with
									clients
								</p>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
